import AirIcon from "@mui/icons-material/Air";
import CalculateIcon from "@mui/icons-material/Calculate";
import Co2OutlinedIcon from "@mui/icons-material/Co2Outlined";
import HeightIcon from "@mui/icons-material/Height";
import IntegrationInstructionsOutlinedIcon from "@mui/icons-material/IntegrationInstructionsOutlined";
import MonitorHeartOutlinedIcon from "@mui/icons-material/MonitorHeartOutlined";
import ScaleIcon from "@mui/icons-material/Scale";
import ThermostatIcon from "@mui/icons-material/Thermostat";
import WaterDropOutlinedIcon from "@mui/icons-material/WaterDropOutlined";
import Flex from "../../../../../../../../components/Flex";
import Text from "../../../../../../../../components/Text";
import useDisclosure from "../../../../../../../../hooks/use-disclosure";
import {
  DEFAULT_ICON_COLOR,
  DEFAULT_KEY_LABEL_COLOR,
} from "../../../../../../../../utils/utils";
import UpsertVitalFormDrawerNew from "../../vitals/UpsertVitalFormDrawerNew";
import LoadingAndEmptyStateWrapper from "./commonWrappers/LoadingAndEmptyStateWrapper";

interface Props {
  vitalsList: any;
  loading: boolean;
}

const Vitals = ({ vitalsList, loading }: Props) => {
  const addVitalDisclosure = useDisclosure();

  // Simple field mapping for labels and icons
  const vitalFieldMap: Record<
    string,
    { label: string; icon: React.ComponentType<any> }
  > = {
    temperature: { label: "Body Temperature", icon: ThermostatIcon },
    height: { label: "Height", icon: HeightIcon },
    weight: { label: "Weight", icon: ScaleIcon },
    heartRate: { label: "Heart Rate", icon: MonitorHeartOutlinedIcon },
    bloodPressure: { label: "Blood Pressure", icon: WaterDropOutlinedIcon },
    bloodGlucose: {
      label: "Blood Glucose",
      icon: IntegrationInstructionsOutlinedIcon,
    },
    respirationRate: { label: "Respiration Rate", icon: Co2OutlinedIcon },
    o2Saturation: { label: "O2 Saturation", icon: AirIcon },
    bmi: { label: "BMI", icon: CalculateIcon },
    peakExpiratoryFlow: { label: "Peak Expiratory Flow", icon: AirIcon },
    forceExpiratoryVolume: { label: "Force Expiratory Volume", icon: AirIcon },
    perfusionIndex: {
      label: "Perfusion Index",
      icon: MonitorHeartOutlinedIcon,
    },
    pulseRate: { label: "Pulse Rate", icon: MonitorHeartOutlinedIcon },
    respiratoryVolume: { label: "Respiratory Volume", icon: AirIcon },
  };

  // Simple function to render vitals from object
  const renderSimpleVitals = (vitalData: any) => {
    const vitalEntries = Object.entries(vitalData).filter(
      ([key, value]) =>
        value !== null && value !== undefined && vitalFieldMap[key]
    );

    if (vitalEntries.length === 0) {
      return null;
    }

    return (
      <Flex flexDirection={"column"} width={"100%"} gap={2} mt={2} ml={1}>
        {vitalEntries.map(([key, value]) => {
          const fieldInfo = vitalFieldMap[key];
          if (!fieldInfo) return null;

          const IconComponent = fieldInfo.icon;
          return (
            <Flex key={key} ml={1}>
              <Text
                isTabletext
                // fontSize={FILED_LABEL_FONTSIZE}
                color={DEFAULT_KEY_LABEL_COLOR}
              >
                <IconComponent
                  sx={{ fontSize: 16, color: DEFAULT_ICON_COLOR, mr: 1 }}
                />
                {`${fieldInfo.label}: ${value}`}
              </Text>
            </Flex>
          );
        })}
      </Flex>
    );
  };

  const hasVitals =
    vitalsList &&
    Object.entries(vitalsList).some(([k, v]) => v != null && vitalFieldMap[k]);

  return (
    <Flex flexDirection={"column"} width={"100%"} gap={1}>
      <UpsertVitalFormDrawerNew disclosureHost={addVitalDisclosure} />
      <LoadingAndEmptyStateWrapper
        loading={loading}
        isEmpty={!hasVitals}
        emptyMessage="No Vitals found"
      />
      {hasVitals && renderSimpleVitals(vitalsList)}
    </Flex>
  );
};

export default Vitals;
